import { generateNodeId, maxDepth } from '@/vantage/components/extensionConfig/reasonCodeManagement/reasonCode.tree';
import type {
  ReasonCodeEditorState,
  ReasonCodeHierarchyContent,
  ReasonCodeLevel,
  ReasonCodeTreeNode,
} from '@/vantage/components/extensionConfig/reasonCodeManagement/reasonCode.types';
import type { ReasonCodeConditionAssignmentOutputV1 } from '@seeqdev/api/model/ReasonCodeConditionAssignmentOutputV1';
import type { ReasonCodeHierarchyInputV1 } from '@seeqdev/api/model/ReasonCodeHierarchyInputV1';
import type { ReasonCodeHierarchyOutputV1 } from '@seeqdev/api/model/ReasonCodeHierarchyOutputV1';
import type { ReasonCodeHierarchyTreeOutputV1 } from '@seeqdev/api/model/ReasonCodeHierarchyTreeOutputV1';
import type { ReasonCodeLabelItemV1 } from '@seeqdev/api/model/ReasonCodeLabelItemV1';
import type { ReasonCodeNodeInputV1 } from '@seeqdev/api/model/ReasonCodeNodeInputV1';

type FlatLabel = { id?: string; name: string; parentId?: string; level: ReasonCodeLevel };

export function treeOutputToEditorState(tree: ReasonCodeHierarchyTreeOutputV1): ReasonCodeEditorState {
  const levels = tree.levels ?? [];

  // Levels arrive name-ordered, not depth-ordered, so derive each depth from parentId pointers, not level index.
  const allLabels: FlatLabel[] = levels.flatMap((level) =>
    (level.labels ?? []).map((label: ReasonCodeLabelItemV1) => ({
      id: label.id,
      name: label.name ?? '',
      parentId: label.parentId || undefined,
      level: { name: level.name ?? '' },
    })),
  );

  const childrenByParent = new Map<string | undefined, FlatLabel[]>();
  for (const label of allLabels) {
    const siblings = childrenByParent.get(label.parentId) ?? [];
    siblings.push(label);
    childrenByParent.set(label.parentId, siblings);
  }

  const levelByDepth: ReasonCodeLevel[] = [];
  const buildNodes = (parentId: string | undefined, depth: number): ReasonCodeTreeNode[] =>
    (childrenByParent.get(parentId) ?? []).map((label) => {
      if (!levelByDepth[depth]) levelByDepth[depth] = label.level;
      return {
        id: label.id ?? generateNodeId(),
        name: label.name,
        children: label.id ? buildNodes(label.id, depth + 1) : [],
      };
    });

  return {
    hierarchyId: tree.id,
    hierarchyName: tree.name ?? '',
    roots: buildNodes(undefined, 0),
    levels: levelByDepth,
  };
}

function buildApiNodes(nodes: ReasonCodeTreeNode[], levels: ReasonCodeLevel[], depth: number): ReasonCodeNodeInputV1[] {
  const level = levels[depth] ?? { name: '' };
  return nodes.map((node) => ({
    name: node.name,
    levelName: level.name,
    // Prompt mirrors the category name; still stored server-side
    levelPrompt: level.name,
    children: node.children.length ? buildApiNodes(node.children, levels, depth + 1) : undefined,
  }));
}

export function editorStateToApiInput(state: Omit<ReasonCodeEditorState, 'hierarchyId'>): ReasonCodeHierarchyInputV1 {
  return {
    name: state.hierarchyName,
    nodes: buildApiNodes(state.roots, state.levels, 0),
  };
}

export type ImportResult =
  | { ok: true; data: ReasonCodeHierarchyContent; ignoredFields?: string[] }
  | { ok: false; messageKey: string; messageValues?: Record<string, string> };

type ImportJsonNode = { label?: unknown; children?: unknown };

function collectIgnoredFields(value: object, knownFields: readonly string[], into: Set<string>): void {
  for (const key of Object.keys(value)) {
    if (!knownFields.includes(key)) into.add(key);
  }
}

function collectIgnoredNodeFields(nodes: unknown[], knownFields: readonly string[], into: Set<string>): void {
  for (const node of nodes) {
    if (node && typeof node === 'object') {
      collectIgnoredFields(node, knownFields, into);
      const { children } = node as { children?: unknown };
      if (Array.isArray(children)) collectIgnoredNodeFields(children, knownFields, into);
    }
  }
}

function importTreeToNodes(nodes: ImportJsonNode[]): ReasonCodeTreeNode[] {
  return nodes.map((node) => ({
    id: generateNodeId(),
    name: String(node?.label ?? '').trim(),
    children: importTreeToNodes(Array.isArray(node?.children) ? node.children : []),
  }));
}

type LegacyImportNode = { label?: unknown; category?: unknown; children?: unknown };

function legacyNodesToTree(nodes: LegacyImportNode[], depth: number, levels: ReasonCodeLevel[]): ReasonCodeTreeNode[] {
  return nodes.map((node) => {
    if (!levels[depth]) levels[depth] = { name: String(node?.category ?? '').trim() };
    return {
      id: generateNodeId(),
      name: String(node?.label ?? '').trim(),
      children: legacyNodesToTree(Array.isArray(node?.children) ? node.children : [], depth + 1, levels),
    };
  });
}

function truncateForMessage(value: string): string {
  return value.length > 30 ? `${value.slice(0, 30)}…` : value;
}

/** Returns the ancestor names of the first empty-named node, or null when every node is named. */
function firstEmptyNamePath(nodes: ReasonCodeTreeNode[], ancestors: string[]): string[] | null {
  for (const node of nodes) {
    if (node.name === '') return ancestors;
    const childPath = firstEmptyNamePath(node.children, [...ancestors, node.name]);
    if (childPath) return childPath;
  }
  return null;
}

function emptyNameFailure(roots: ReasonCodeTreeNode[]): ImportResult | null {
  const path = firstEmptyNamePath(roots, []);
  if (!path) return null;
  return path.length
    ? {
        ok: false,
        messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.EMPTY_NODE_NAME_UNDER',
        messageValues: { path: truncateForMessage(path.join(' > ')) },
      }
    : { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.EMPTY_NODE_NAME' };
}

/** Parses the `{ name?, levels?, reasonCodes }` JSON export shape, or the legacy array-wrapped shape, into editor state. */
export function parseHierarchyImport(text: string): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.INVALID_JSON' };
  }

  // The legacy shape is array-wrapped, naming levels via per-node category instead of a hoisted levels array.
  if (Array.isArray(parsed)) {
    const legacyRoot: unknown = parsed[0];
    if (!legacyRoot || typeof legacyRoot !== 'object') {
      return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.JSON_SHAPE' };
    }
    const { reasonCodes } = legacyRoot as { reasonCodes?: unknown };
    if (!Array.isArray(reasonCodes)) {
      return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.JSON_SHAPE' };
    }
    if (reasonCodes.length === 0) {
      return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.EMPTY' };
    }
    const ignored = new Set<string>();
    collectIgnoredFields(legacyRoot, ['conditionIds', 'reasonCodes'], ignored);
    collectIgnoredNodeFields(reasonCodes, ['label', 'category', 'prompt', 'children'], ignored);
    const levels: ReasonCodeLevel[] = [];
    const roots = legacyNodesToTree(reasonCodes, 0, levels);
    return (
      emptyNameFailure(roots) ?? {
        ok: true,
        data: { hierarchyName: '', levels, roots },
        ...(ignored.size ? { ignoredFields: [...ignored].sort() } : {}),
      }
    );
  }

  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.JSON_SHAPE' };
  }

  const obj = parsed as { name?: unknown; levels?: { name?: unknown }[]; reasonCodes?: ImportJsonNode[] };
  if (!Array.isArray(obj.reasonCodes)) {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.JSON_SHAPE' };
  }
  if (obj.reasonCodes.length === 0) {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.EMPTY' };
  }

  const roots = importTreeToNodes(obj.reasonCodes);
  const failure = emptyNameFailure(roots);
  if (failure) return failure;

  const ignored = new Set<string>();
  collectIgnoredFields(obj, ['name', 'levels', 'reasonCodes'], ignored);
  collectIgnoredNodeFields(obj.reasonCodes, ['label', 'children'], ignored);
  const levels: ReasonCodeLevel[] = [];
  if (Array.isArray(obj.levels)) {
    for (const level of obj.levels) {
      if (level && typeof level === 'object') collectIgnoredFields(level, ['name'], ignored);
      levels.push({ name: String(level?.name ?? '').trim() });
    }
  }

  return {
    ok: true,
    data: {
      hierarchyName: typeof obj.name === 'string' ? obj.name : '',
      levels,
      roots,
    },
    ...(ignored.size ? { ignoredFields: [...ignored].sort() } : {}),
  };
}

/** Splits CSV text into rows of cells, honoring double-quoted fields (with "" escapes) and CRLF. */
function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char !== '\r') {
      field += char;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Parses a forward-fill CSV: the header row names the levels, and each row nests under the most recent node in the column to its left. */
export function parseHierarchyCsv(text: string): ImportResult {
  // Strip a leading UTF-8 BOM (Excel and others prepend one) before tokenizing.
  const rows = parseCsvRows(text.replace(/^\uFEFF/, '')).filter((cells) => cells.some((cell) => cell.trim() !== ''));
  if (rows.length < 2) {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.CSV_NO_ROWS' };
  }

  const header = rows[0].map((cell) => cell.trim());
  const levels: ReasonCodeLevel[] = header.map((name) => ({ name }));
  const roots: ReasonCodeTreeNode[] = [];
  const lastAtLevel: (ReasonCodeTreeNode | undefined)[] = [];
  for (const cells of rows.slice(1)) {
    const extraCell = cells.slice(header.length).find((cell) => cell.trim() !== '');
    if (extraCell !== undefined) {
      return {
        ok: false,
        messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.CSV_EXTRA_CELLS',
        messageValues: { value: truncateForMessage(extraCell.trim()) },
      };
    }
    for (let col = 0; col < header.length; col++) {
      const value = (cells[col] ?? '').trim();
      if (value) {
        const node: ReasonCodeTreeNode = { id: generateNodeId(), name: value, children: [] };
        const parent = col > 0 ? lastAtLevel[col - 1] : undefined;
        if (col > 0 && !parent) {
          return {
            ok: false,
            messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.CSV_ORPHAN_ROW',
            messageValues: { value: truncateForMessage(value) },
          };
        }
        if (parent) parent.children.push(node);
        else roots.push(node);
        lastAtLevel.length = col + 1;
        lastAtLevel[col] = node;
      }
    }
  }

  if (roots.length === 0) {
    return { ok: false, messageKey: 'VANTAGE.EXTENSION_CONFIG.REASON_CODES.IMPORT_ERROR.EMPTY' };
  }
  return { ok: true, data: { hierarchyName: '', levels, roots } };
}

function escapeCsvCell(value: string): string {
  return /["\n\r,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Serializes a hierarchy to the forward-fill CSV: one node per row, its name in its depth column. */
export function hierarchyToCsv({ levels, roots }: ReasonCodeHierarchyContent): string {
  const width = Math.max(levels.length, maxDepth(roots), 1);
  const rows = [Array.from({ length: width }, (_, depth) => escapeCsvCell(levels[depth]?.name ?? '')).join(',')];
  const walk = (nodes: ReasonCodeTreeNode[], depth: number): void => {
    for (const node of nodes) {
      rows.push(Array.from({ length: width }, (_, col) => (col === depth ? escapeCsvCell(node.name) : '')).join(','));
      walk(node.children, depth + 1);
    }
  };
  walk(roots, 0);
  return rows.join('\n');
}

type ExportJsonNode = { label: string; children?: ExportJsonNode[] };

function nodesToJson(nodes: ReasonCodeTreeNode[]): ExportJsonNode[] {
  return nodes.map((node) =>
    node.children.length ? { label: node.name, children: nodesToJson(node.children) } : { label: node.name },
  );
}

/** Serializes a hierarchy to the `{ name, levels, reasonCodes }` JSON export shape. */
export function hierarchyToJson({ hierarchyName, levels, roots }: ReasonCodeHierarchyContent): string {
  return JSON.stringify(
    { name: hierarchyName, levels: levels.map((level) => ({ name: level.name })), reasonCodes: nodesToJson(roots) },
    null,
    2,
  );
}

/** The hierarchy assigned as the workbook's room default, if any. */
export function roomDefaultHierarchy(
  conditionAssignments: ReasonCodeConditionAssignmentOutputV1[],
  reasonCodeHierarchies: ReasonCodeHierarchyOutputV1[],
): ReasonCodeHierarchyOutputV1 | undefined {
  const defaultAssignment = conditionAssignments.find((assignment) => assignment.conditionId == null);
  return defaultAssignment
    ? reasonCodeHierarchies.find((hierarchy) => hierarchy.id === defaultAssignment.hierarchyCategoryId)
    : undefined;
}

/** Maps each condition id to the name of the hierarchy it is currently on, excluding the one being edited. */
export function conditionAssignmentNames(
  conditionAssignments: ReasonCodeConditionAssignmentOutputV1[],
  reasonCodeHierarchies: ReasonCodeHierarchyOutputV1[],
  currentHierarchyId: string | undefined,
): Record<string, string> {
  const nameById = new Map(reasonCodeHierarchies.map((hierarchy) => [hierarchy.id, hierarchy.name ?? '']));
  const names: Record<string, string> = {};
  for (const assignment of conditionAssignments) {
    const { conditionId, hierarchyCategoryId } = assignment;
    if (conditionId && hierarchyCategoryId && hierarchyCategoryId !== currentHierarchyId) {
      const name = nameById.get(hierarchyCategoryId);
      if (name) names[conditionId] = name;
    }
  }
  return names;
}
